import React from 'react';
import { getRealUrlPrefix, useApiClient, createApiErrorHandler } from '@m43/core';
import { useBootstrapState } from '../bootstrap/bootstrap-provider';
import { useToast } from '@cad-arc';
import { AuthenticationResource } from '@mark43/rms-api';
import { useApplicationType } from '../common/hooks/use-application-type';
import { electronBridge } from '../electron-bridge/electron-bridge';
import { normalizeUrl } from '../common/helpers/utils';

type App = 'FIRST_RESPONDER' | 'FIRST_RESPONDER_NEXT' | 'CAD' | 'RMS';

export type OpenDepartmentLink = (options: {
    path: string;
    app?: App;
    tenantStrategy?: 'PREFER_RMS_TENANT';
}) => void;

function replaceDepartmentSubdomain(url: string, subdomain: string): string {
    const urlObject = new URL(url);
    const [, ...rest] = urlObject.host.split('.');
    return [subdomain, ...rest].join('.');
}

function pathSegmentFromApp(app: App): string {
    switch (app) {
        case 'CAD':
            return 'cad-next';
        case 'FIRST_RESPONDER':
            return 'first-responder';
        case 'FIRST_RESPONDER_NEXT':
            return 'first-responder-next';
        case 'RMS':
            return 'rms';
        default:
            throw new Error('did not recognize provided app');
    }
}

function constructCrossLoginUrl(
    token: string,
    departmentSubdomain: string,
    options: Parameters<OpenDepartmentLink>[0]
): string {
    const urlPrefix = replaceDepartmentSubdomain(getRealUrlPrefix(), departmentSubdomain);
    // %23 is the encoded version of #
    const path = `/cross-login/${token}?redirect=${pathSegmentFromApp(options.app ?? 'CAD')}/%23${
        options.path
    }`;
    return normalizeUrl(
        `https://${urlPrefix}/${pathSegmentFromApp(options.app ?? 'CAD')}/login/#${path}`
    );
}

export function useOpenDepartmentLink(): OpenDepartmentLink {
    const { isElectron } = useApplicationType();
    const apiClient = useApiClient();
    const bootstrapState = useBootstrapState();
    const toast = useToast();
    const openDepartmentLink = React.useMemo(() => {
        const ret: OpenDepartmentLink = (options) => {
            if (isElectron) {
                const departmentSubdomain =
                    options.tenantStrategy === 'PREFER_RMS_TENANT' &&
                    bootstrapState.userHasAccountInRmsTenant
                        ? bootstrapState.rmsDepartmentAgencyView.subDomain
                        : bootstrapState.cadDepartmentAgencyView.subDomain;
                apiClient
                    .request<AuthenticationResource.CreateCrossLoginTokenForTargetDepartment>({
                        service: 'RMS',
                        method: 'POST',
                        url: `auth/cross_login/create_token/${departmentSubdomain}/`,
                    })
                    .then((resp) => {
                        const token = resp.data.data;
                        if (token) {
                            const urlToUse = constructCrossLoginUrl(
                                token,
                                departmentSubdomain,
                                options
                            );
                            electronBridge?.openUrlInBrowser(urlToUse);
                        }
                    })
                    .catch(
                        createApiErrorHandler(() => {
                            toast({
                                status: 'error',
                                description: 'Error opening department link',
                            });
                        })
                    );
            } else {
                if (
                    options.tenantStrategy === 'PREFER_RMS_TENANT' &&
                    bootstrapState.userHasAccountInRmsTenant
                ) {
                    const externalWindow = window.open('about:blank');
                    const departmentSubdomain = bootstrapState.rmsDepartmentAgencyView.subDomain;
                    apiClient
                        .request<AuthenticationResource.CreateCrossLoginTokenForTargetDepartment>({
                            service: 'RMS',
                            method: 'POST',
                            url: `auth/cross_login/create_token/${departmentSubdomain}/`,
                        })
                        .then((resp) => {
                            const token = resp.data.data;
                            if (token) {
                                const urlToUse = constructCrossLoginUrl(
                                    token,
                                    departmentSubdomain,
                                    options
                                );
                                if (externalWindow) {
                                    externalWindow.location.href = urlToUse;
                                }
                            }
                        })
                        .catch(
                            createApiErrorHandler(() => {
                                toast({
                                    status: 'error',
                                    description: 'Error opening department link',
                                });
                            })
                        );
                } else {
                    const urlToOpen = normalizeUrl(
                        `${getRealUrlPrefix()}/${pathSegmentFromApp(options.app ?? 'CAD')}/#/${
                            options.path
                        }`
                    );
                    window.open(urlToOpen);
                }
            }
        };
        return ret;
    }, [
        apiClient,
        bootstrapState.cadDepartmentAgencyView.subDomain,
        bootstrapState.rmsDepartmentAgencyView.subDomain,
        bootstrapState.userHasAccountInRmsTenant,
        toast,
        isElectron,
    ]);
    return openDepartmentLink;
}
